import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const FIELD_TYPES = ["text", "email", "phone", "textarea", "number", "date", "select", "radio", "checkbox", "file", "product_list", "group"];

const EMPTY = {
  name: "",
  title: "",
  description: "",
  active: true,
  fields: [],
};

export default function Templates() {
  const [templates, setTemplates] = useState(null);
  const [editing, setEditing] = useState(null); // template being edited or null
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/admin/templates").then(({ data }) => setTemplates(data)).catch((e) => toast.error(apiError(e)));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!editing.name.trim() || !editing.title.trim()) {
      toast.error("Name and form title are required");
      return;
    }
    setSaving(true);
    try {
      const { id, created_at, updated_at, ...body } = editing;
      if (id) {
        await api.put(`/admin/templates/${id}`, body);
      } else {
        await api.post("/admin/templates", body);
      }
      toast.success("Template saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Businesses will no longer see it.`)) return;
    try {
      await api.delete(`/admin/templates/${t.id}`);
      toast.success("Template deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const setField = (idx, patch) => {
    const fields = editing.fields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
    setEditing({ ...editing, fields });
  };

  const addField = () => {
    setEditing({
      ...editing,
      fields: [...editing.fields, { id: `tf_${Date.now()}`, type: "text", label: "New field", placeholder: "", required: false, options: [] }],
    });
  };

  return (
    <div data-testid="templates-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-black">Form templates</h1>
          <p className="text-sm text-neutral-500 mt-1">Templates businesses can apply in the form builder, then customise to their needs.</p>
        </div>
        <Button onClick={() => setEditing({ ...EMPTY })} className="rounded-full" data-testid="new-template-button">
          <Plus className="h-4 w-4 mr-1.5" /> New template
        </Button>
      </div>

      <div className="mt-8 bg-white border border-[#E5E5E5] rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E5E5E5] text-left text-xs uppercase tracking-widest text-neutral-400">
              <th className="px-5 py-3">Template</th>
              <th className="px-5 py-3">Fields</th>
              <th className="px-5 py-3">Status</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E5]">
            {!templates ? (
              <tr><td className="px-5 py-6 text-neutral-400" colSpan={4}>Loading…</td></tr>
            ) : templates.length === 0 ? (
              <tr><td className="px-5 py-6 text-neutral-400" colSpan={4} data-testid="templates-empty">No templates yet.</td></tr>
            ) : (
              templates.map((t) => (
                <tr key={t.id} data-testid={`template-row-${t.id}`}>
                  <td className="px-5 py-3.5">
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-neutral-400 mt-0.5">{t.description}</div>
                  </td>
                  <td className="px-5 py-3.5 text-neutral-500">{t.fields.length} field(s)</td>
                  <td className="px-5 py-3.5">
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${t.active ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-500"}`} data-testid={`template-status-${t.id}`}>
                      {t.active ? "Active" : "Hidden"}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-right whitespace-nowrap">
                    <Button variant="outline" size="sm" className="rounded-full mr-2" onClick={() => setEditing(JSON.parse(JSON.stringify(t)))} data-testid={`template-edit-${t.id}`}>
                      Edit
                    </Button>
                    <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200" onClick={() => remove(t)} data-testid={`template-delete-${t.id}`}>
                      Delete
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit template" : "New template"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Template name</Label>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Appointment request" data-testid="template-name-input" />
                </div>
                <div className="space-y-2">
                  <Label>Form title (business sees this)</Label>
                  <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Book an appointment" data-testid="template-title-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Description</Label>
                <Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} data-testid="template-description-input" />
              </div>
              <div className="flex items-center justify-between">
                <Label>Visible to businesses</Label>
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} data-testid="template-active-switch" />
              </div>

              <div className="space-y-2">
                <Label>Fields</Label>
                {editing.fields.map((f, i) => (
                  <div key={f.id} className="border border-[#E5E5E5] rounded-lg p-2.5 space-y-2" data-testid={`template-field-${i}`}>
                    <div className="flex items-center gap-1.5">
                      <Input value={f.label} onChange={(e) => setField(i, { label: e.target.value })} className="h-8 text-sm" placeholder="Field label" data-testid={`template-field-label-${i}`} />
                      <button onClick={() => setEditing({ ...editing, fields: editing.fields.filter((_, j) => j !== i) })} className="text-neutral-400 hover:text-red-600 shrink-0" data-testid={`template-field-delete-${i}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="flex items-center gap-3">
                      <select
                        value={f.type}
                        onChange={(e) => setField(i, { type: e.target.value })}
                        className="text-xs border border-[#E5E5E5] rounded-md px-2 py-1.5 bg-white"
                        data-testid={`template-field-type-${i}`}
                      >
                        {FIELD_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <label className="flex items-center gap-1.5 text-xs text-neutral-500">
                        <Checkbox checked={f.required} onCheckedChange={(v) => setField(i, { required: !!v })} data-testid={`template-field-required-${i}`} />
                        Required
                      </label>
                    </div>
                    {["select", "radio", "checkbox"].includes(f.type) && (
                      <Input
                        value={(f.options || []).join(", ")}
                        onChange={(e) => setField(i, { options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
                        className="h-8 text-xs"
                        placeholder="Options, comma separated"
                        data-testid={`template-field-options-${i}`}
                      />
                    )}
                  </div>
                ))}
                <Button variant="outline" size="sm" className="rounded-lg" onClick={addField} data-testid="template-add-field">
                  <Plus className="h-3.5 w-3.5 mr-1" /> Add field
                </Button>
              </div>

              <Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="template-save-button">
                {saving ? "Saving…" : "Save template"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
